import { useTranslation } from 'react-i18next'
import { Platforms, Roles } from '@/utils'
import { useAppSelector } from '@/redux/hooks'
import { useParams } from 'react-router'
import { ActionButton, AddBoxIcon, Base } from '@itsrever/design-system'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
  isFlagForReviewEnabled,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { CancelModal } from './cancel-modal'
import IncidentModal from './report-incident/incident-modal'
import { RequestDocumentsModal } from '@/ui/components/request-documents-modal'
import { FlagForReview } from '../flag-for-review/flag-for-review'
import { Customer } from '@/domain/customer'

const {
  Button,
  MenuRoot,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuItem,
  DialogRoot,
  DialogPopup,
  DialogPortal,
  DialogBackdrop
} = Base

const MORE_ACTIONS_BUTTON_WIDTH = 130
const ACTION_BUTTON_WIDTH = 150
const FLEX_GAP = 4
const MAX_VISIBLE_BUTTONS = 3

interface ActionButton {
  content: ReactNode
  onClick: () => void
  show: boolean
  disabled?: boolean
}
interface ActionMenuProps {
  isRestocked: boolean
  reviewMode: boolean
  restockMode: boolean
  isKeepYourItem: boolean
  uploadDocsEnabled: boolean
  setRestockMode: (value: boolean) => void
  unauthorized: boolean
  authPending: boolean
  canceled: boolean
  isCancelable: boolean
  isExchangeOnly: boolean
  hasExecutedStoreCreditRefund: boolean
  hasReviews: boolean
  customer?: Customer
  isClaim: boolean
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  isRestocked,
  reviewMode,
  restockMode,
  isKeepYourItem,
  uploadDocsEnabled,
  setRestockMode,
  unauthorized,
  authPending,
  canceled,
  isCancelable,
  isExchangeOnly,
  hasExecutedStoreCreditRefund,
  customer,
  hasReviews,
  isClaim
}) => {
  const { t } = useTranslation()
  const { id } = useParams()

  const portalTrackingParams = new URLSearchParams({
    processId: id || '',
    email: customer?.email || ''
  })

  const { isFlagEnabled } = useFeatureFlag(isFlagForReviewEnabled)

  const role = useAppSelector(
    (store) => store.userApi.getMe.response.user?.role
  )

  const isUserAuthorizedToReview =
    role === Roles.ReadWrite || role === Roles.Reviewer

  const showFlagForReview =
    isFlagEnabled &&
    !hasReviews &&
    isUserAuthorizedToReview &&
    !isKeepYourItem &&
    !authPending &&
    !unauthorized &&
    !canceled &&
    !isClaim

  const containerRef = useRef<HTMLDivElement>(null)
  const flagForReviewRef = useRef<HTMLDivElement>(null)
  const [spaceForActions, setSpaceForActions] = useState(0)

  const recalculateSpace = useCallback(() => {
    const container = containerRef.current
    const parent = container?.parentElement
    if (!container || !parent) return

    const parentWidth = parent.getBoundingClientRect().width
    const containerRect = container.getBoundingClientRect()

    const processInfo = container.previousElementSibling
    const processInfoRect = processInfo?.getBoundingClientRect()
    const isOnSameRow =
      processInfoRect && Math.abs(containerRect.top - processInfoRect.top) < 10
    const availableWidth = isOnSameRow
      ? parentWidth - (processInfoRect?.width ?? 0)
      : parentWidth

    const flagForReviewWidth =
      flagForReviewRef.current?.getBoundingClientRect().width ?? 0

    setSpaceForActions(availableWidth - flagForReviewWidth)
  }, [])

  useEffect(() => {
    const parent = containerRef.current?.parentElement
    if (!parent) return
    const resizeObserver = new ResizeObserver(recalculateSpace)
    resizeObserver.observe(parent)
    return () => resizeObserver.disconnect()
  }, [recalculateSpace])

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )
  const allowRestockSettings = settings.restocking_available
  const platform = settings.platform

  const restockButtonEnabled =
    (platform === Platforms.Shopify || platform === Platforms.Prestashop) &&
    !reviewMode &&
    !isRestocked &&
    allowRestockSettings &&
    role === Roles.ReadWrite &&
    !isKeepYourItem

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [showIncidentModal, setShowIncidentModal] = useState(false)
  const [showRequestDocsModal, setShowRequestDocsModal] = useState(false)

  const showRequestDocsButton =
    !unauthorized && !canceled && uploadDocsEnabled && !authPending
  const showCancelButton =
    !unauthorized && !canceled && isCancelable && !isClaim
  const showRestockButton =
    !unauthorized && !canceled && restockButtonEnabled && !isClaim

  const requestDocsButton: ActionButton = {
    content: (
      <>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          className="size-4"
        >
          <path d="M4.5 6.75c0-.69.56-1.25 1.25-1.25h1.514c.473 0 .906.268 1.118.691l.17.342c.297.592.903.967 1.566.967h4.132c.69 0 1.25.56 1.25 1.25 0 .414.336.75.75.75s.75-.336.75-.75c0-1.519-1.231-2.75-2.75-2.75h-4.132c-.095 0-.181-.053-.224-.138l-.17-.342c-.466-.931-1.418-1.52-2.46-1.52h-1.514c-1.519 0-2.75 1.231-2.75 2.75v6.5c0 1.519 1.231 2.75 2.75 2.75h5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-5c-.69 0-1.25-.56-1.25-1.25v-6.5Z" />
          <path d="M15.75 13.31v2.94c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-2.94l-.72.72c-.293.293-.767.293-1.06 0-.293-.293-.293-.767 0-1.06l2-2c.293-.293.767-.293 1.06 0l2 2c.293.293.293.767 0 1.06-.293.293-.767.293-1.06 0l-.72-.72Z" />
        </svg>
        {t('process_details_page.request_docs')}
      </>
    ),
    onClick: () => setShowRequestDocsModal(true),
    show: showRequestDocsButton
  }
  const portalTrackingButton: ActionButton = {
    content: (
      <>
        <svg
          width="18px"
          height="18px"
          viewBox="0 0 24 24"
          strokeWidth="2"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          color="currentColor"
        >
          <path
            d="M3 13C6.6 5 17.4 5 21 13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></path>
          <path
            d="M12 17C10.3431 17 9 15.6569 9 14C9 12.3431 10.3431 11 12 11C13.6569 11 15 12.3431 15 14C15 15.6569 13.6569 17 12 17Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></path>
        </svg>
        {t('process_details_page.portal_tracking')}
      </>
    ),
    onClick: () =>
      window.open(
        `https://returns.itsrever.com/${settings.slug}/tracking?${portalTrackingParams.toString()}`,
        '_blank'
      ),
    show: true
  }
  const cancelButton: ActionButton = {
    content: (
      <>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          fill="currentColor"
          viewBox="0 0 256 256"
        >
          <path d="M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z"></path>
        </svg>
        {t('cancel_processes.cancel_button')}
      </>
    ),
    onClick: () => setIsCancelModalOpen(true),
    show: showCancelButton
  }
  const restockButton: ActionButton = {
    content: (
      <>
        <AddBoxIcon className="size-4" />
        {t('process_details_page.restock')}
      </>
    ),
    onClick: () => setRestockMode(!restockMode),
    show: showRestockButton ?? false,
    disabled: isRestocked || restockMode
  }
  const reportIncidentButton: ActionButton = {
    content: (
      <>
        <svg
          stroke="currentColor"
          fill="none"
          strokeWidth="2"
          viewBox="0 0 24 24"
          strokeLinecap="round"
          strokeLinejoin="round"
          height="15"
          width="15"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
          <path d="M12 9v4"></path>
          <path d="M12 17h.01"></path>
        </svg>
        {t('process_details_page.report_incident.tooltip_label')}
      </>
    ),
    onClick: () => setShowIncidentModal(true),
    show: true
  }

  const allButtons: ActionButton[] = [
    portalTrackingButton,
    requestDocsButton,
    restockButton,
    reportIncidentButton,
    cancelButton
  ]
  const shownButtons = allButtons.filter((button) => button.show)

  const maxFit = Math.floor(spaceForActions / (ACTION_BUTTON_WIDTH + FLEX_GAP))
  const allFit = maxFit >= shownButtons.length
  const buttonsToShow = allFit
    ? shownButtons.length
    : Math.max(
        0,
        Math.min(
          MAX_VISIBLE_BUTTONS,
          Math.floor(
            (spaceForActions - MORE_ACTIONS_BUTTON_WIDTH) /
              (ACTION_BUTTON_WIDTH + FLEX_GAP)
          )
        )
      )

  const barButtons = shownButtons.slice(0, buttonsToShow)
  const menuButtons = shownButtons.slice(buttonsToShow)

  return (
    <>
      <div
        ref={containerRef}
        className="flex flex-1 items-start justify-end gap-1"
      >
        {showFlagForReview ? (
          <div ref={flagForReviewRef}>
            <FlagForReview />
          </div>
        ) : null}
        {barButtons.map((button, index) => (
          <Button
            key={index}
            variant="secondary"
            size="sm"
            onClick={button.onClick}
            disabled={button.disabled ?? false}
          >
            {button.content}
          </Button>
        ))}
        {menuButtons.length > 0 ? (
          <MenuRoot>
            <MenuTrigger
              render={
                <Button
                  variant="secondary"
                  size="sm"
                  className="data-[pressed]:bg-neutral-100"
                />
              }
            >
              {t('process_details_page.more_actions')}
              <svg
                width="16px"
                height="16px"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                color="currentColor"
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </MenuTrigger>
            <MenuPortal>
              <MenuPositioner sideOffset={8} align="end">
                <MenuPopup>
                  {menuButtons.map((button, index) => (
                    <MenuItem
                      key={index}
                      onClick={button.onClick}
                      disabled={button.disabled ?? false}
                      className="font-roobert font-medium"
                    >
                      {button.content}
                    </MenuItem>
                  ))}
                </MenuPopup>
              </MenuPositioner>
            </MenuPortal>
          </MenuRoot>
        ) : null}
      </div>

      <DialogRoot open={isCancelModalOpen} onOpenChange={setIsCancelModalOpen}>
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="scale-[calc(1-0.1*var(--nested-dialogs))]">
            <CancelModal
              onClose={() => setIsCancelModalOpen(false)}
              processId={id ?? ''}
              isExchangeOnly={isExchangeOnly}
              hasExecutedStoreCreditRefund={hasExecutedStoreCreditRefund}
            />
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
      <IncidentModal
        isOpen={showIncidentModal}
        onRequestClose={() => setShowIncidentModal(false)}
        processId={id ?? ''}
      />
      <RequestDocumentsModal
        isOpen={showRequestDocsModal}
        onRequestClose={() => setShowRequestDocsModal(false)}
        processId={id ?? ''}
      />
    </>
  )
}

export default ActionMenu
